import React, { useEffect, useRef, useState } from 'react';
import './css/channel.css';

export default function MiniAnalyser({ stream, audioContext, audioStreamAvailable }) {
    const [instantVolume, setInstantVolume] = useState(0);
    const [slowVolume, setSlowVolume] = useState(0);
    const gainNodeRef = useRef(null);
    const sliderVolumeRef = useRef(null);
    const analyserRef = useRef(null);
    const reqIdRef = useRef(0);
    const monitorIdRef = useRef(0);
    const timeDataRef = useRef([]);
    const bufferLengthRef = useRef(0);
    const maxLevelRef = useRef(0.0);
    const timeMaxLevelSetRef = useRef(0);
    const fftSize = 256;
    const meterHeight = 28;
    const meterTrackWidth = 3;
    const meterMaskHeight = (1 - instantVolume) * meterHeight;
    const meterMarkerTop = Math.max(0, Math.min(meterHeight - 2, (1 - slowVolume) * meterHeight - 1));

    useEffect(() => {
        if (!stream) {
            if (gainNodeRef.current) {
                cancelAnimationFrame(reqIdRef.current);
                clearTimeout(monitorIdRef.current);
                sliderVolumeRef.current?.disconnect();
                gainNodeRef.current?.disconnect();
                analyserRef.current?.disconnect();
                gainNodeRef.current = null;
                sliderVolumeRef.current = null;
                analyserRef.current = null;
                reqIdRef.current = 0;
                timeDataRef.current = [];
                bufferLengthRef.current = 0;
                maxLevelRef.current = 0.0;
                setInstantVolume(0);
                setSlowVolume(0);
            }
        } else {
            setupGraph(stream);
            reqIdRef.current = requestAnimationFrame(monitorMiniVolume);
        }

        return () => {
            cancelAnimationFrame(reqIdRef.current);
        };
    }, [stream]);

    function setupGraph(stream) {
        gainNodeRef.current = audioContext.createGain();
        sliderVolumeRef.current = audioContext.createGain();

        analyserRef.current = audioContext.createAnalyser();
        analyserRef.current.minDecibels = -100;
        analyserRef.current.maxDecibels = 0;
        analyserRef.current.smoothingTimeConstant = 0.8;
        analyserRef.current.fftSize = fftSize;
        bufferLengthRef.current = analyserRef.current.frequencyBinCount;
        timeDataRef.current = new Uint8Array(bufferLengthRef.current);

        stream.connect(gainNodeRef.current);
        gainNodeRef.current.connect(analyserRef.current);
        analyserRef.current.connect(sliderVolumeRef.current);
        timeMaxLevelSetRef.current = Date.now();

        audioStreamAvailable(sliderVolumeRef.current);
    }

    function monitorMiniVolume() {
        if (!analyserRef.current) return;

        analyserRef.current.getByteFrequencyData(timeDataRef.current);

        let max = 0.0;
        for (let i = 0; i < bufferLengthRef.current; i++) {
            if (timeDataRef.current[i] > max) max = timeDataRef.current[i];
        }
        const instant = max / 255;

        if (instant > maxLevelRef.current) {
            maxLevelRef.current = instant;
            timeMaxLevelSetRef.current = Date.now();
        }
        if ((Date.now() - timeMaxLevelSetRef.current) > 2000) {
            maxLevelRef.current = instant;
            timeMaxLevelSetRef.current = Date.now();
        }

        setInstantVolume(instant);
        setSlowVolume(maxLevelRef.current);

        if (stream !== null) {
            reqIdRef.current = requestAnimationFrame(monitorMiniVolume);
        }
    }

    return (
        <div style={{ position: 'relative', width: 12, height: meterHeight, flexShrink: 0 }}>
            <div style={{ width: meterTrackWidth, height: meterMaskHeight, backgroundColor: '#333333',
                          position: 'absolute', left: 4, top: 0, zIndex: 2 }} />
            <div style={{ width: meterTrackWidth, height: meterHeight,
                          backgroundImage: 'linear-gradient(red 0%, red 12%, yellow 12%, yellow 37%, gray 37%, gray 100%)',
                          position: 'absolute', left: 4, top: 0, zIndex: 1 }} />
            <div style={{ width: 0, height: 0, borderTop: '2px solid transparent',
                          borderBottom: '2px solid transparent', borderLeft: '2px solid white',
                          position: 'absolute', left: 9, top: meterMarkerTop, zIndex: 3 }} />
        </div>
    );
}
